import {
  Suspense,
  lazy,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "../../@/components/ui/tabs";
import ClassSchedule from "../shared/ClassSchedule";
import LiveClassesSkeleton from "../shared/LiveClassesSkeleton";
import AddClassForm from "../forms/addClass";
import { useAuth } from "@clerk/clerk-react";
import { api_endpoint } from "../constants/constant";
import { toast } from "sonner";
import {
  keepPreviousData,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { apiRequest } from "@/lib/apiRequest";
import { useSelector } from "react-redux";
import { fromZonedTime } from "date-fns-tz";
import { fetchAllPaginatedItems } from "@/lib/fetchAllPaginatedItems";
import { useSidebarPageTitle } from "@/shared/useSidebarPageTitle";

const RecordingTable = lazy(() => import("../shared/RecordingTable"));

export default function LiveClassesPage() {
  const sidebarTitle = useSidebarPageTitle();
  const [activeTab, setActiveTab] = useState("upcoming");
  const [shouldRenderPreviousTab, setShouldRenderPreviousTab] = useState(false);
  const { getToken } = useAuth();
  const queryClient = useQueryClient();
  const classDetailsCacheRef = useRef(new Map());
  const fetchClassesInFlightRef = useRef({ key: null, promise: null });

  const [adminLoading, setAdminLoading] = useState(false);
  const [adminError, setAdminError] = useState(null);

  // Admin-mode TM filter/sort (upcoming sessions)
  const [instructors, setInstructors] = useState([]);
  const [adminTmFilter, setAdminTmFilter] = useState("ALL");
  const [adminTmSort, setAdminTmSort] = useState("date");
  const [adminUpcomingAll, setAdminUpcomingAll] = useState([]);
  const [adminClassesAll, setAdminClassesAll] = useState([]);

  // Backend paginated upcoming sessions state
  const [upcomingPage, setUpcomingPage] = useState(1);
  const [upcomingPageSize, setUpcomingPageSize] = useState(5);
  const upcomingPageRef = useRef(upcomingPage);
  const upcomingPageSizeRef = useRef(upcomingPageSize);
  const previousPrivilegedModeRef = useRef(false);

  const roles = useSelector((s) => s.auth.roles) || [];
  const isAdmin = roles.includes("Admin");
  const isManager = roles.includes("Manager");
  const isPrivileged = isAdmin || isManager;

  const readModeFlags = () => {
    if (typeof window === "undefined") {
      return { adminMode: "off", managerMode: "off" };
    }
    const adminMode = localStorage.getItem("zindua_admin_mode") || "off";
    const managerMode = localStorage.getItem("zindua_manager_mode") || "off";
    return { adminMode, managerMode };
  };

  const [modeFlags, setModeFlags] = useState(() => readModeFlags());
  useEffect(() => {
    const onModeChanged = () => setModeFlags(readModeFlags());
    window.addEventListener("zindua-mode-changed", onModeChanged);
    window.addEventListener("storage", onModeChanged);
    return () => {
      window.removeEventListener("zindua-mode-changed", onModeChanged);
      window.removeEventListener("storage", onModeChanged);
    };
  }, []);

  const isPrivilegedModeOn = useMemo(() => {
    if (!isPrivileged) return false;
    if (isAdmin) return modeFlags.adminMode === "on";
    if (isManager) return modeFlags.managerMode === "on";
    return false;
  }, [
    isPrivileged,
    isAdmin,
    isManager,
    modeFlags.adminMode,
    modeFlags.managerMode,
  ]);

  useEffect(() => {
    upcomingPageRef.current = upcomingPage;
  }, [upcomingPage]);

  useEffect(() => {
    upcomingPageSizeRef.current = upcomingPageSize;
  }, [upcomingPageSize]);

  useEffect(() => {
    if (!isPrivilegedModeOn) return;
    let cancelled = false;

    (async () => {
      try {
        const users = await queryClient.fetchQuery({
          queryKey: ["users", "role", "Instructor"],
          staleTime: 10 * 60 * 1000,
          queryFn: async () => {
            const token = await getToken();
            if (!token) return [];

            const reqHeaders = {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            };

            const result = await fetchAllPaginatedItems({
              url: api_endpoint + `/user/users/role/${"Instructor"}`,
              token,
              selectItems: (data) => data?.users ?? [],
              mode: "skip",
              pageSize: 200,
              headers: reqHeaders,
            });

            return Array.isArray(result) ? result : [];
          },
        });

        if (!cancelled) setInstructors(Array.isArray(users) ? users : []);
      } catch (e) {
        if (!cancelled) setInstructors([]);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [getToken, isPrivilegedModeOn]);

  const instructorByClerkId = useMemo(() => {
    const map = new Map();
    (Array.isArray(instructors) ? instructors : []).forEach((u) => {
      const keys = [
        u?.clerk_id,
        u?.clerkId,
        u?.clerkID,
        u?.user_id,
        u?.userId,
        u?.id,
      ]
        .filter((v) => v !== null && v !== undefined && `${v}`.trim() !== "")
        .map((v) => String(v));

      keys.forEach((k) => map.set(k, u));
    });
    return map;
  }, [instructors]);

  // Avoid re-creating heavy callbacks (and retriggering effects) whenever instructor mapping updates.
  const instructorByClerkIdRef = useRef(instructorByClerkId);
  useEffect(() => {
    instructorByClerkIdRef.current = instructorByClerkId;
  }, [instructorByClerkId]);

  const resolveTmLabel = useCallback(
    (session) => {
      const first = session?.owner_first_name ?? session?.ownerFirstName ?? "";
      const last = session?.owner_last_name ?? session?.ownerLastName ?? "";
      const fullName = `${first} ${last}`.trim();
      if (fullName) return fullName;

      const ownerId =
        session?.owner_id ?? session?.ownerId ?? session?.__owner_id ?? "";
      const u = ownerId ? instructorByClerkId.get(String(ownerId)) : null;
      const mappedName = u
        ? `${u?.first_name ?? u?.firstName ?? ""} ${u?.last_name ?? u?.lastName ?? ""}`.trim()
        : "";
      if (mappedName) return mappedName;

      const mappedEmail = String(
        u?.email ?? u?.primary_email ?? u?.primaryEmail ?? "",
      ).trim();
      if (mappedEmail) return mappedEmail;

      return "Unknown Instructor";
    },
    [instructorByClerkId],
  );

  const adminTmFilterOptions = useMemo(() => {
    if (!isPrivilegedModeOn) return [];

    const ownerIds = new Set([
      ...(Array.isArray(adminClassesAll) ? adminClassesAll : [])
        .map((c) => c?.owner_id ?? c?.ownerId ?? c?.tm_id ?? c?.tmId)
        .filter(Boolean)
        .map((v) => String(v)),
      ...(Array.isArray(adminUpcomingAll) ? adminUpcomingAll : [])
        .map(
          (s) =>
            s?.owner_id ??
            s?.ownerId ??
            s?.__owner_id ??
            s?.tm_id ??
            s?.tmId ??
            s?.__tm_id,
        )
        .filter(Boolean)
        .map((v) => String(v)),
    ]);

    const getInstructorIds = (u) =>
      [u?.clerk_id, u?.clerkId, u?.clerkID, u?.user_id, u?.userId, u?.id]
        .filter((v) => v !== null && v !== undefined && `${v}`.trim() !== "")
        .map((v) => String(v));

    const getInstructorLabel = (u) => {
      const first = u?.first_name ?? u?.firstName ?? "";
      const last = u?.last_name ?? u?.lastName ?? "";
      const name = `${first} ${last}`.trim();
      if (name) return name;
      return String(
        u?.email ?? u?.primary_email ?? u?.primaryEmail ?? "",
      ).trim();
    };

    const optionsByValue = new Map();
    (Array.isArray(instructors) ? instructors : []).forEach((u) => {
      const ids = getInstructorIds(u);
      if (!ids.length) return;
      const value = ids.find((id) => ownerIds.has(id)) || ids[0];
      const label = getInstructorLabel(u);
      if (!label) return;
      if (!optionsByValue.has(value)) {
        optionsByValue.set(value, { value, label });
      }
    });

    const options = Array.from(optionsByValue.values());

    options.sort((a, b) => String(a.label).localeCompare(String(b.label)));
    return [{ value: "ALL", label: "All Instructors" }, ...options];
  }, [adminClassesAll, instructors, isPrivilegedModeOn]);

  // Admin upcoming: memoized filter/sort so pagination doesn't re-sort huge arrays
  const adminUpcomingFilteredSorted = useMemo(() => {
    const ownerIdFilter = String(adminTmFilter || "ALL");
    const source = Array.isArray(adminUpcomingAll) ? adminUpcomingAll : [];
    const filtered =
      ownerIdFilter === "ALL"
        ? source
        : source.filter((s) => {
          const ownerId = String(
            s?.owner_id ?? s?.ownerId ?? s?.__owner_id ?? "",
          );
          const tmId = String(
            s?.tm_id ??
            s?.tmId ??
            s?.assigned_tm_id ??
            s?.assignedTmId ??
            s?.assigned_tm ??
            s?.assignedTm ??
            s?.assigned_reviewer ??
            s?.assignedReviewer ??
            s?.technical_mentor_id ??
            s?.technicalMentorId ??
            s?.__tm_id ??
            "",
          );
          return ownerId === ownerIdFilter || tmId === ownerIdFilter;
        });

    const sortBy = String(adminTmSort || "date");
    const sorted = [...filtered];
    if (sortBy === "tm_asc" || sortBy === "tm_desc") {
      sorted.sort((a, b) => {
        const aLabel = resolveTmLabel(a);
        const bLabel = resolveTmLabel(b);
        const cmp = String(aLabel).localeCompare(String(bLabel));
        if (cmp !== 0) return sortBy === "tm_desc" ? -cmp : cmp;
        const aKey = `${a.session_date}T${a.session_start_time}`;
        const bKey = `${b.session_date}T${b.session_start_time}`;
        return aKey.localeCompare(bKey);
      });
    } else {
      sorted.sort((a, b) => {
        const aKey = `${a.session_date}T${a.session_start_time}`;
        const bKey = `${b.session_date}T${b.session_start_time}`;
        return aKey.localeCompare(bKey);
      });
    }

    return sorted;
  }, [adminTmFilter, adminTmSort, adminUpcomingAll, resolveTmLabel]);

  const adminUpcomingTotalCount = adminUpcomingFilteredSorted.length;
  const adminUpcomingTotalPages = useMemo(
    () =>
      Math.max(
        1,
        Math.ceil(adminUpcomingTotalCount / (Number(upcomingPageSize) || 5)) ||
        1,
      ),
    [adminUpcomingTotalCount, upcomingPageSize],
  );

  useEffect(() => {
    if (!isPrivilegedModeOn) return;
    setUpcomingPage((p) => {
      const safe = Math.max(
        1,
        Math.min(Number(p) || 1, adminUpcomingTotalPages),
      );
      return safe;
    });
  }, [adminUpcomingTotalPages, isPrivilegedModeOn]);

  const adminUpcomingPaged = useMemo(() => {
    const safePage = Math.max(
      1,
      Math.min(Number(upcomingPage) || 1, adminUpcomingTotalPages),
    );
    const pageSize = Math.max(1, Number(upcomingPageSize) || 5);
    const startIndex = (safePage - 1) * pageSize;
    return adminUpcomingFilteredSorted.slice(startIndex, startIndex + pageSize);
  }, [
    adminUpcomingFilteredSorted,
    adminUpcomingTotalPages,
    upcomingPage,
    upcomingPageSize,
  ]);
  const TAB_STORAGE_KEY = "zindua_liveclasses_active_tab";

  // Sync active tab from URL query param (preferred), then localStorage (fallback) on mount.
  useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      const tabParam = params.get("tab");
      if (tabParam === "upcoming" || tabParam === "previous") {
        setActiveTab(tabParam);
        return;
      }

      const stored = localStorage.getItem(TAB_STORAGE_KEY);
      if (stored === "upcoming" || stored === "previous") {
        setActiveTab(stored);
      }
    } catch (e) {
      // ignore
    }
  }, []);

  // Persist active tab (URL + localStorage) when it changes.
  useEffect(() => {
    try {
      localStorage.setItem(TAB_STORAGE_KEY, activeTab);
      const params = new URLSearchParams(window.location.search);
      params.set("tab", activeTab);
      const newUrl = window.location.pathname + "?" + params.toString();
      window.history.replaceState({}, "", newUrl);
    } catch (e) {
      // ignore
    }
  }, [activeTab]);

  useEffect(() => {
    if (activeTab === "previous") {
      setShouldRenderPreviousTab(true);
    }
  }, [activeTab]);
  const normalizeUpcomingSession = useCallback((raw) => {
    const session = raw || {};
    return {
      ...session,
      owner_id:
        session.owner_id ?? session.ownerId ?? session.__owner_id ?? null,
      tm_id:
        session.tm_id ??
        session.tmId ??
        session.assigned_tm_id ??
        session.assignedTmId ??
        session.assigned_tm ??
        session.assignedTm ??
        session.assigned_reviewer ??
        session.assignedReviewer ??
        session.__tm_id ??
        null,
      class_id:
        session.class_id ??
        session.classId ??
        session.__class_id ??
        session.class?.id ??
        session.class?.class_id ??
        null,
      session_id: session.session_id ?? session.sessionId ?? session.id,
      class_title:
        session.class_title ??
        session.classTitle ??
        session.title ??
        session.name,
      owner_first_name:
        session.owner_first_name ??
        session.ownerFirstName ??
        session.facilitator_first_name,
      owner_last_name:
        session.owner_last_name ??
        session.ownerLastName ??
        session.facilitator_last_name,
      class_location:
        session.class_location ?? session.location ?? session.classLocation,
      class_type: session.class_type ?? session.classType ?? session.type,
      session_date: session.session_date ?? session.sessionDate ?? session.date,
      session_start_time:
        session.session_start_time ??
        session.sessionStartTime ??
        session.start_time,
      session_end_time:
        session.session_end_time ?? session.sessionEndTime ?? session.end_time,
      session_duration_minutes:
        session.session_duration_minutes ??
        session.duration_minutes ??
        session.durationMinutes ??
        session.duration,
      session_meeting_link:
        session.session_meeting_link ??
        session.join_url ??
        session.live_url ??
        session.meeting_url ??
        session.link,
      session_room: session.session_room ?? session.room,
      recurrence_pattern:
        session.recurrence_pattern ?? session.recurrencePattern,
    };
  }, []);

  const upcomingQuery = useQuery({
    queryKey: ["liveclass", "upcoming", upcomingPage, upcomingPageSize],
    enabled: !isPrivilegedModeOn,
    staleTime: 60 * 1000,
    placeholderData: keepPreviousData,
    queryFn: async ({ signal }) => {
      const payload = await apiRequest(getToken, {
        path: "/liveclass/upcoming-sessions",
        auth: "required",
        signal,
        params: { page: upcomingPage, page_size: upcomingPageSize },
      });

      console.log("[LIVECLASSES RAW] mode=normal endpoint=/liveclass/upcoming-sessions", payload);
      return payload;
    },
  });

  const upcomingClasses = useMemo(() => {
    const items = Array.isArray(upcomingQuery.data?.items)
      ? upcomingQuery.data.items
      : [];
    return items.map(normalizeUpcomingSession);
  }, [normalizeUpcomingSession, upcomingQuery.data]);

  const upcomingTotalPages = Number(upcomingQuery.data?.total_pages) || 1;
  const upcomingTotalCount = Number(upcomingQuery.data?.total_count) || 0;
  const upcomingError = upcomingQuery.isError
    ? String(upcomingQuery.error?.message || "Failed to fetch classes")
    : null;

  // Warm the class-details cache in the background so Edit opens faster.
  useEffect(() => {
    if (isPrivilegedModeOn) return;
    const uniqueClassIds = Array.from(
      new Set(
        (Array.isArray(upcomingClasses) ? upcomingClasses : [])
          .map((it) => it?.class_id ?? it?.classId ?? it?.id)
          .filter(Boolean)
          .map((id) => String(id)),
      ),
    );

    const idsToPrefetch = uniqueClassIds
      .slice(0, 10)
      .filter((id) => !classDetailsCacheRef.current.has(id));

    if (!idsToPrefetch.length) return;

    let cancelled = false;
    let idleHandle = null;
    let timeoutHandle = null;

    const runPrefetch = async () => {
      for (const id of idsToPrefetch) {
        if (cancelled) return;
        try {
          const detailsResponse = await queryClient.prefetchQuery({
            queryKey: ["liveclass", "details", id],
            staleTime: 10 * 60 * 1000,
            queryFn: ({ signal }) =>
              apiRequest(getToken, {
                path: `/liveclass/${id}`,
                auth: "required",
                signal,
              }),
          });

          const details = detailsResponse?.class ?? detailsResponse ?? null;
          if (details) classDetailsCacheRef.current.set(id, details);
        } catch {
          // ignore
        }
      }
    };

    if (
      typeof window !== "undefined" &&
      typeof window.requestIdleCallback === "function"
    ) {
      idleHandle = window.requestIdleCallback(runPrefetch, { timeout: 2500 });
    } else {
      timeoutHandle = window.setTimeout(runPrefetch, 1200);
    }

    return () => {
      cancelled = true;
      if (
        idleHandle != null &&
        typeof window.cancelIdleCallback === "function"
      ) {
        window.cancelIdleCallback(idleHandle);
      }
      if (timeoutHandle != null) {
        window.clearTimeout(timeoutHandle);
      }
    };
  }, [getToken, isPrivilegedModeOn, queryClient, upcomingClasses]);

  const fetchClasses = useCallback(
    async (options = {}) => {
      try {
        const resolvedPage =
          Number(options?.page ?? upcomingPageRef.current) || 1;
        const resolvedPageSize =
          Number(options?.pageSize ?? upcomingPageSizeRef.current) || 5;

        // Non-privileged mode is handled by TanStack Query.
        if (!isPrivilegedModeOn) {
          if (options?.page != null) setUpcomingPage(resolvedPage);
          if (options?.pageSize != null) setUpcomingPageSize(resolvedPageSize);
          queryClient.invalidateQueries({
            queryKey: ["liveclass", "upcoming"],
          });
          return;
        }

        const inFlightKey = `admin-upcoming|${resolvedPage}|${resolvedPageSize}`;
        if (
          fetchClassesInFlightRef.current?.promise &&
          fetchClassesInFlightRef.current.key === inFlightKey
        ) {
          return fetchClassesInFlightRef.current.promise;
        }

        setAdminLoading(true);
        setAdminError(null);

        const promise = (async () => {
          // Treat API `session_date` + `session_start_time` as UTC (backend stores times in UTC).
          const SOURCE_TIMEZONE = "UTC";
          const toDateTimeMs = (dateValue, timeValue) => {
            const datePartRaw = String(dateValue || "").trim();
            const datePart = datePartRaw.includes("T")
              ? datePartRaw.split("T")[0]
              : datePartRaw;
            const timePartRaw = String(timeValue || "").trim();
            if (!datePart || !timePartRaw) return null;

            const [y, m, d] = datePart.split("-").map((n) => Number(n));
            if (![y, m, d].every((n) => Number.isFinite(n))) return null;

            const timeParts = timePartRaw.split(":");
            const hh = Number(timeParts[0]);
            const mm = Number(timeParts[1]);
            const ss = Number(timeParts[2] ?? 0);
            if (![hh, mm, ss].every((n) => Number.isFinite(n))) return null;

            const wallClock = new Date(y, m - 1, d, hh, mm, ss, 0);
            if (Number.isNaN(wallClock.getTime())) return null;
            const absolute = fromZonedTime(wallClock, SOURCE_TIMEZONE);
            const ms = absolute?.getTime?.();
            return Number.isFinite(ms) ? ms : null;
          };

          const extractArray = (value) => {
            if (Array.isArray(value)) return value;
            if (!value || typeof value !== "object") return [];
            const candidates = [
              value.items,
              value.sessions,
              value.upcoming_sessions,
              value.data,
              value.results,
              value.past_sessions,
            ];
            for (const candidate of candidates) {
              if (Array.isArray(candidate)) return candidate;
              if (candidate && typeof candidate === "object") {
                if (Array.isArray(candidate.items)) return candidate.items;
                if (Array.isArray(candidate.sessions))
                  return candidate.sessions;
                if (Array.isArray(candidate.data)) return candidate.data;
              }
            }
            return [];
          };

          if (isPrivilegedModeOn) {
            let token = await getToken();
            try {
              token = (await getToken?.({ skipCache: true })) || token;
            } catch {
              // ignore
            }
            if (!token) throw new Error("No auth token found");
            const reqHeaders = {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            };

            const allClassesResponse = await queryClient.fetchQuery({
              queryKey: ["liveclass", "all"],
              staleTime: 10 * 60 * 1000,
              queryFn: ({ signal }) =>
                apiRequest(getToken, {
                  path: "/liveclass/all",
                  auth: "required",
                  signal,
                  retryOnceOn401: true,
                }),
            });

            console.log("[LIVECLASSES RAW] mode=privileged endpoint=/liveclass/all", allClassesResponse);

            const classListRaw =
              allClassesResponse?.classes ||
              allClassesResponse?.items ||
              allClassesResponse?.data ||
              allClassesResponse ||
              [];
            const classList = Array.isArray(classListRaw) ? classListRaw : [];
            setAdminClassesAll(classList);

            const classOwnerById = new Map(
              classList
                .map((c) => {
                  const classId = c?.class_id ?? c?.id ?? c?.classId;
                  const ownerId = c?.owner_id ?? c?.ownerId ?? null;
                  return [
                    classId != null ? String(classId) : "",
                    ownerId != null ? String(ownerId) : "",
                  ];
                })
                .filter(([cid]) => cid),
            );
            const classTmById = new Map(
              classList
                .map((c) => {
                  const classId = c?.class_id ?? c?.id ?? c?.classId;
                  const tmId =
                    c?.tm_id ??
                    c?.tmId ??
                    c?.assigned_tm_id ??
                    c?.assignedTmId ??
                    c?.assigned_tm ??
                    c?.assignedTm ??
                    c?.assigned_reviewer ??
                    c?.assignedReviewer ??
                    null;
                  return [
                    classId != null ? String(classId) : "",
                    tmId != null ? String(tmId) : "",
                  ];
                })
                .filter(([cid]) => cid),
            );

            const classIds = Array.from(
              new Set(
                classList
                  .map((c) => c?.class_id ?? c?.id ?? c?.classId)
                  .filter(
                    (id) =>
                      id !== null && id !== undefined && `${id}`.trim() !== "",
                  )
                  .map((id) => String(id)),
              ),
            );

            if (!classIds.length) {
              setUpcomingPage(1);
              setUpcomingPageSize(resolvedPageSize);
              setAdminUpcomingAll([]);
              return;
            }

            const enrichOwnerName = (session) => {
              const ownerId =
                session?.owner_id ?? session?.ownerId ?? session?.__owner_id;
              const hasName = Boolean(
                (session?.owner_first_name || "").trim() ||
                (session?.owner_last_name || "").trim(),
              );
              if (hasName || !ownerId) return session;
              const u = instructorByClerkIdRef.current?.get?.(String(ownerId));
              if (!u) return session;
              return {
                ...session,
                owner_first_name: session?.owner_first_name ?? u?.first_name,
                owner_last_name: session?.owner_last_name ?? u?.last_name,
              };
            };

            // Batch requests to avoid hammering the API/gateway.
            // UX: avoid pushing partial, re-sorted lists into state (this causes visible jumping).
            // We do a fast first pass (first page only per class) then background-fill the rest.
            const seen = new Set();
            const accumulated = [];
            const batchSize = 4;
            // API contract: `/liveclass/upcoming-sessions/admin/{class_id}` paginates via `skip`/`limit`.
            // Keep the admin API page size small and stable; UI paging is applied client-side after aggregation.
            const adminApiPageSize = 5;

            const initialTarget = Math.max(
              1,
              resolvedPageSize * Math.max(1, resolvedPage),
            );
            let didInitialRender = false;
            let nextStartIndex = 0;

            // Prefer fetching the most recently updated classes first so the initial render is more likely
            // to include the sessions users care about.
            const classRecencyMs = (c) => {
              const raw =
                c?.updated_at ??
                c?.updatedAt ??
                c?.modified_at ??
                c?.modifiedAt ??
                c?.created_at ??
                c?.createdAt ??
                null;
              const ms = raw ? Date.parse(String(raw)) : NaN;
              return Number.isFinite(ms) ? ms : 0;
            };
            const classById = new Map(
              classList
                .map((c) => {
                  const classId = c?.class_id ?? c?.id ?? c?.classId;
                  return [classId != null ? String(classId) : "", c];
                })
                .filter(([cid]) => cid),
            );
            const orderedClassIds = [...classIds].sort((a, b) => {
              const ca = classById.get(String(a));
              const cb = classById.get(String(b));
              return classRecencyMs(cb) - classRecencyMs(ca);
            });

            // Phase 1: page 1 only for enough classes to fill the requested page, then render early.
            for (let i = 0; i < classIds.length; i += batchSize) {
              const batch = orderedClassIds.slice(i, i + batchSize);
              // eslint-disable-next-line no-await-in-loop
              const batchResults = await Promise.allSettled(
                batch.map((classId) =>
                  queryClient.fetchQuery({
                    queryKey: [
                      "liveclass",
                      "upcoming-sessions",
                      "admin",
                      classId,
                      "skip",
                      adminApiPageSize,
                      "page1",
                    ],
                    staleTime: 10 * 60 * 1000,
                    queryFn: () =>
                      fetchAllPaginatedItems({
                        url:
                          api_endpoint +
                          `/liveclass/upcoming-sessions/admin/${classId}`,
                        token,
                        headers: reqHeaders,
                        mode: "skip",
                        pageSize: adminApiPageSize,
                        maxPages: 1,
                        dedupe: false,
                        selectItems: extractArray,
                      }),
                  }),
                ),
              );

              batchResults.forEach((res, idx) => {
                if (res.status === "fulfilled") return;
                const cid = batch[idx];
                console.error(
                  `[/liveclass/upcoming-sessions/admin/${cid}] failed:`,
                  res.reason?.response?.data ?? res.reason,
                );
              });

              const allUpcomingRaw = batch.flatMap((classId, idx) => {
                const result = batchResults[idx];
                if (!result || result.status !== "fulfilled") return [];
                const rows = Array.isArray(result.value)
                  ? result.value
                  : extractArray(result.value);
                const ownerId = classOwnerById.get(String(classId)) || "";
                const tmId = classTmById.get(String(classId)) || "";
                return rows.map((row) => ({
                  ...row,
                  __class_id: classId,
                  __owner_id: ownerId,
                  __tm_id: tmId,
                }));
              });

              const allUpcomingNormalized = allUpcomingRaw
                .map(normalizeUpcomingSession)
                .filter((s) => s?.session_date && s?.session_start_time);

              const nowMs = Date.now();
              const skewMs = 5 * 60 * 1000;
              for (const session of allUpcomingNormalized) {
                const ms = toDateTimeMs(
                  session?.session_date,
                  session?.session_start_time,
                );
                if (ms !== null && ms < nowMs - skewMs) continue;

                const key = [
                  session?.class_id ?? "",
                  session?.session_id ?? "",
                  session?.session_date ?? "",
                  session?.session_start_time ?? "",
                ]
                  .map((v) => String(v ?? ""))
                  .join("|");

                if (key && seen.has(key)) continue;
                if (key) seen.add(key);
                accumulated.push(enrichOwnerName(session));
              }

              if (!didInitialRender && accumulated.length >= initialTarget) {
                didInitialRender = true;
                nextStartIndex = i + batchSize;
                setAdminUpcomingAll([...accumulated]);
                setUpcomingPage(Math.max(1, resolvedPage));
                setUpcomingPageSize(resolvedPageSize);
                setAdminLoading(false);
                break;
              }
            }

            if (!didInitialRender) {
              // First render (fallback): we didn't hit the target, so render what we have.
              setAdminUpcomingAll([...accumulated]);
              setUpcomingPage(Math.max(1, resolvedPage));
              setUpcomingPageSize(resolvedPageSize);
              setAdminLoading(false);
              nextStartIndex = classIds.length;
            }

            // Background-fill: finish remaining classes' page1 + remaining pages; update once at the end.
            (async () => {
              try {
                const seenBg = new Set(seen);
                const accumulatedBg = [...accumulated];

                // Finish remaining classes' first pages (so phase 2 doesn't miss anything).
                for (
                  let i = nextStartIndex;
                  i < classIds.length;
                  i += batchSize
                ) {
                  const batch = orderedClassIds.slice(i, i + batchSize);
                  // eslint-disable-next-line no-await-in-loop
                  const batchResults = await Promise.allSettled(
                    batch.map((classId) =>
                      queryClient.fetchQuery({
                        queryKey: [
                          "liveclass",
                          "upcoming-sessions",
                          "admin",
                          classId,
                          "skip",
                          adminApiPageSize,
                          "page1",
                        ],
                        staleTime: 10 * 60 * 1000,
                        queryFn: () =>
                          fetchAllPaginatedItems({
                            url:
                              api_endpoint +
                              `/liveclass/upcoming-sessions/admin/${classId}`,
                            token,
                            headers: reqHeaders,
                            mode: "skip",
                            pageSize: adminApiPageSize,
                            maxPages: 1,
                            dedupe: false,
                            selectItems: extractArray,
                          }),
                      }),
                    ),
                  );

                  const allUpcomingRaw = batch.flatMap((classId, idx) => {
                    const result = batchResults[idx];
                    if (!result || result.status !== "fulfilled") return [];
                    const rows = Array.isArray(result.value)
                      ? result.value
                      : extractArray(result.value);
                    const ownerId = classOwnerById.get(String(classId)) || "";
                    const tmId = classTmById.get(String(classId)) || "";
                    return rows.map((row) => ({
                      ...row,
                      __class_id: classId,
                      __owner_id: ownerId,
                      __tm_id: tmId,
                    }));
                  });

                  const allUpcomingNormalized = allUpcomingRaw
                    .map(normalizeUpcomingSession)
                    .filter((s) => s?.session_date && s?.session_start_time);

                  const nowMs = Date.now();
                  const skewMs = 5 * 60 * 1000;
                  for (const session of allUpcomingNormalized) {
                    const ms = toDateTimeMs(
                      session?.session_date,
                      session?.session_start_time,
                    );
                    if (ms !== null && ms < nowMs - skewMs) continue;

                    const key = [
                      session?.class_id ?? "",
                      session?.session_id ?? "",
                      session?.session_date ?? "",
                      session?.session_start_time ?? "",
                    ]
                      .map((v) => String(v ?? ""))
                      .join("|");

                    if (key && seenBg.has(key)) continue;
                    if (key) seenBg.add(key);
                    accumulatedBg.push(enrichOwnerName(session));
                  }
                }

                for (let i = 0; i < classIds.length; i += batchSize) {
                  const batch = orderedClassIds.slice(i, i + batchSize);
                  // eslint-disable-next-line no-await-in-loop
                  const batchResults = await Promise.allSettled(
                    batch.map((classId) =>
                      queryClient.fetchQuery({
                        queryKey: [
                          "liveclass",
                          "upcoming-sessions",
                          "admin",
                          classId,
                          "skip",
                          adminApiPageSize,
                          "rest",
                        ],
                        staleTime: 10 * 60 * 1000,
                        queryFn: () =>
                          fetchAllPaginatedItems({
                            url:
                              api_endpoint +
                              `/liveclass/upcoming-sessions/admin/${classId}`,
                            token,
                            headers: reqHeaders,
                            mode: "skip",
                            startSkip: adminApiPageSize,
                            pageSize: adminApiPageSize,
                            maxPages: 200,
                            dedupe: false,
                            selectItems: extractArray,
                          }),
                      }),
                    ),
                  );

                  const allUpcomingRaw = batch.flatMap((classId, idx) => {
                    const result = batchResults[idx];
                    if (!result || result.status !== "fulfilled") return [];
                    const rows = Array.isArray(result.value)
                      ? result.value
                      : extractArray(result.value);
                    const ownerId = classOwnerById.get(String(classId)) || "";
                    const tmId = classTmById.get(String(classId)) || "";
                    return rows.map((row) => ({
                      ...row,
                      __class_id: classId,
                      __owner_id: ownerId,
                      __tm_id: tmId,
                    }));
                  });

                  const allUpcomingNormalized = allUpcomingRaw
                    .map(normalizeUpcomingSession)
                    .filter((s) => s?.session_date && s?.session_start_time);

                  const nowMs = Date.now();
                  const skewMs = 5 * 60 * 1000;
                  for (const session of allUpcomingNormalized) {
                    const ms = toDateTimeMs(
                      session?.session_date,
                      session?.session_start_time,
                    );
                    if (ms !== null && ms < nowMs - skewMs) continue;

                    const key = [
                      session?.class_id ?? "",
                      session?.session_id ?? "",
                      session?.session_date ?? "",
                      session?.session_start_time ?? "",
                    ]
                      .map((v) => String(v ?? ""))
                      .join("|");

                    if (key && seenBg.has(key)) continue;
                    if (key) seenBg.add(key);
                    accumulatedBg.push(enrichOwnerName(session));
                  }
                }

                setAdminUpcomingAll([...accumulatedBg]);
              } catch {
                // ignore
              }
            })();

            return;
          }
        })();

        fetchClassesInFlightRef.current = { key: inFlightKey, promise };
        promise.finally(() => {
          if (fetchClassesInFlightRef.current?.promise === promise) {
            fetchClassesInFlightRef.current = { key: null, promise: null };
          }
        });

        return await promise;
      } catch (error) {
        console.error("Error fetching classes", error);
        setAdminError("Failed to fetch classes");
      } finally {
        setAdminLoading(false);
      }
    },
    [getToken, isPrivilegedModeOn, normalizeUpcomingSession, queryClient],
  );

  // Refetch only when switching between privileged/non-privileged mode.
  useEffect(() => {
    const wasPrivileged = Boolean(previousPrivilegedModeRef.current);
    previousPrivilegedModeRef.current = Boolean(isPrivilegedModeOn);
    if (wasPrivileged === Boolean(isPrivilegedModeOn)) return;

    if (isPrivilegedModeOn) {
      setUpcomingPage(1);
      const hasAdminData =
        Array.isArray(adminUpcomingAll) && adminUpcomingAll.length > 0;
      if (!hasAdminData) {
        fetchClasses({ page: 1, pageSize: upcomingPageSizeRef.current });
      }
    } else {
      // Back to non-privileged mode: rely on query cache but ensure a refresh soon.
      queryClient.invalidateQueries({ queryKey: ["liveclass", "upcoming"] });
    }
  }, [adminUpcomingAll, fetchClasses, isPrivilegedModeOn, queryClient]);

  const handleUpcomingPageChange = (nextPage) => {
    const total = isPrivilegedModeOn
      ? adminUpcomingTotalPages
      : upcomingTotalPages || 1;
    const safePage = Math.max(1, Math.min(Number(nextPage) || 1, total));
    setUpcomingPage(safePage);
  };

  const handleUpcomingRowsPerPageChange = (nextSize) => {
    const safeSize = Math.max(1, Number(nextSize) || 5);
    setUpcomingPageSize(safeSize);
    setUpcomingPage(1);
  };

  const fetchClassDetails = async (classId, options = {}) => {
    try {
      const { silent = false } = options || {};
      const force = Boolean(options?.force);
      const cacheKey = String(classId ?? "").trim();
      if (!force && cacheKey && classDetailsCacheRef.current.has(cacheKey)) {
        return classDetailsCacheRef.current.get(cacheKey);
      }

      const detailsResponse = await queryClient.fetchQuery({
        queryKey: ["liveclass", "details", cacheKey],
        queryFn: ({ signal }) =>
          apiRequest(getToken, {
            path: `/liveclass/${classId}`,
            auth: "required",
            signal,
          }),
      });

      const details = detailsResponse?.class ?? detailsResponse ?? null;
      if (cacheKey && details) {
        classDetailsCacheRef.current.set(cacheKey, details);
      }
      return details;
    } catch (error) {
      const status = error?.response?.status;
      const data = error?.response?.data;
      console.error("Error fetching class details", {
        message: error?.message,
        status,
        data,
        url: error?.config?.url,
        method: error?.config?.method,
      });
      if (!silent) {
        toast.error(
          status
            ? `Failed to fetch class details (HTTP ${status})`
            : "Failed to fetch class details",
        );
      }
      return null;
    }
  };

  return (
    <div className="flex flex-col transition-all duration-300 ease-in-out">
      <main className="overflow-y-auto bg-transparent rounded-b-4xl">
        <h1>{sidebarTitle || "Manage Classes"}</h1>
        <p className="text-md text-(--neutral-700)">
          Manage your upcoming classes, view recordings, and track attendance.
        </p>
        <Tabs
          value={activeTab}
          onValueChange={setActiveTab}
          className="w-full space-y-4 mt-3"
        >
          <div className="flex flex-col md:flex-row w-full gap-2 md:items-center md:justify-between">
            <TabsList className="w-full md:w-auto">
              <TabsTrigger value="upcoming" className="w-full md:w-auto">
                Upcoming Classes
              </TabsTrigger>
              <TabsTrigger value="previous" className="w-full md:w-auto">
                Previous Classes
              </TabsTrigger>
            </TabsList>

            {activeTab === "upcoming" && (
              <div className="w-full md:w-auto mt-2 md:mt-0">
                <AddClassForm fetchClasses={fetchClasses} />
              </div>
            )}
          </div>
          <div className="transition-opacity duration-300">
            <TabsContent value="upcoming" forceMount className="mt-0">
              {(isPrivilegedModeOn ? adminLoading : upcomingQuery.isPending) ? (
                <LiveClassesSkeleton />
              ) : (
                <ClassSchedule
                  classes={
                    isPrivilegedModeOn ? adminUpcomingPaged : upcomingClasses
                  }
                  loading={Boolean(
                    isPrivilegedModeOn ? adminLoading : upcomingQuery.isPending,
                  )}
                  error={isPrivilegedModeOn ? adminError : upcomingError}
                  fetchClasses={fetchClasses}
                  fetchClassDetails={fetchClassDetails}
                  isAdminModeOn={Boolean(
                    isAdmin && modeFlags.adminMode === "on",
                  )}
                  currentPage={upcomingPage}
                  rowsPerPage={upcomingPageSize}
                  totalPages={
                    isPrivilegedModeOn
                      ? adminUpcomingTotalPages
                      : upcomingTotalPages
                  }
                  totalRows={
                    isPrivilegedModeOn
                      ? adminUpcomingTotalCount
                      : upcomingTotalCount
                  }
                  onPageChange={handleUpcomingPageChange}
                  onRowsPerPageChange={handleUpcomingRowsPerPageChange}
                  extraFilters={
                    isPrivilegedModeOn
                      ? [
                        {
                          label: "Instructor",
                          value: adminTmFilter,
                          options: adminTmFilterOptions,
                          onChange: (v) => {
                            setAdminTmFilter(v);
                            setUpcomingPage(1);
                          },
                          onClear: () => {
                            setAdminTmFilter("ALL");
                            setUpcomingPage(1);
                          },
                        },
                      ]
                      : []
                  }
                  extraSorts={
                    isPrivilegedModeOn
                      ? [
                        {
                          label: "Sort by",
                          value: adminTmSort,
                          options: [
                            { value: "date", label: "Date/Time" },
                            { value: "tm_asc", label: "Instructor (A-Z)" },
                            { value: "tm_desc", label: "Instructor (Z-A)" },
                          ],
                          onChange: (v) => {
                            setAdminTmSort(v);
                            setUpcomingPage(1);
                          },
                          onClear: () => {
                            setAdminTmSort("date");
                            setUpcomingPage(1);
                          },
                        },
                      ]
                      : []
                  }
                />
              )}
            </TabsContent>
            <TabsContent value="previous" forceMount className="mt-0">
              {shouldRenderPreviousTab ? (
                <Suspense fallback={<LiveClassesSkeleton />}>
                  <RecordingTable />
                </Suspense>
              ) : null}
            </TabsContent>
          </div>
        </Tabs>
      </main>
    </div>
  );
}
